type TParams = Record<string, string | number | boolean>;
type THeaders = Record<string, string>;
type TBody = object;

export interface IBaseRequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE',
    params?: TParams,
    body?: TBody,
    headers?: THeaders,
}

export type TRequestOptions = Omit<IBaseRequestOptions, 'params' | 'body' | 'method'>

export class ApiInstance {
    baseUrl: string;
    baseHeaders: Record<string, string>;
    middlewares: ((req: RequestInit) => RequestInit)[] = [];

    constructor(baseUrl: string, baseHeaders: THeaders = {
        'Content-Type': 'application/json'
    }) {
        this.baseUrl = baseUrl;
        this.baseHeaders = baseHeaders;
    }

    public setBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;

        return this;
    }

    public setBaseHeader(key: string, value: string) {
        this.baseHeaders[key] = value;

        return this;
    }

    protected async request<T extends unknown>(
        endpoint: string,
        { method = 'GET', params, body, headers }: IBaseRequestOptions = {}
    ): Promise<T> {
        const url = new URL(`${this.baseUrl}${endpoint}`);

        if (params) {
            for (const key of Object.keys(params)) {
                url.searchParams.append(key, params[key].toString());
            }
        }

        const obj = this.middlewares.reduce((prev, cur) => {
            return Object.assign(prev, cur(prev));
        }, {
            method,
            body: JSON.stringify(body),
            headers: {
                ...this.baseHeaders,
                ...headers
            }
        });

        const req = await fetch(url, obj);

        const json = await req.json() as T;

        if (req.status !== 200) throw new Error(JSON.stringify(json));

        return json;
    }

    get<T>(endpoint: string, params?: TParams, options: TRequestOptions = {}): Promise<T> {
        return this.request<T>(endpoint, { ...options, params, method: 'GET' });
    }

    post<T>(endpoint: string, body?: TBody, options: TRequestOptions = {}): Promise<T> {
        return this.request<T>(endpoint, { ...options, body, method: 'POST' });
    }

    put<T>(endpoint: string, body?: TBody, options: TRequestOptions = {}): Promise<T> {
        return this.request<T>(endpoint, { ...options, body, method: 'PUT' });
    }

    delete<T>(endpoint: string, body?: TBody, options: TRequestOptions = {}): Promise<T> {
        return this.request<T>(endpoint, { ...options, body, method: 'DELETE' });
    }
}